import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import isArchived from 'archive/isArchived';
import {userCreatableProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import {Schema} from 'schemas/SchemaConstants';
import {ConnectedSearchQuery} from 'search/__generated/ConnectedSearchQuery';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

type ProcessTaskSchema = ConnectedSearchQuery['processTaskSchemasSupportingPlatformFeature'][number];

interface CreateFromSchemaButtonProps {
  schemas: Array<Schema> | null;
  onSelect: (schema: Schema) => void;
}

export default function CreateFromSchemaButton(props: CreateFromSchemaButtonProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const instantiableSchemas = props.schemas
    ? props.schemas.filter((schema) => {
        const executionType = (schema as ProcessTaskSchema).executionType;
        if (executionType && !userCreatableProcessTaskSchema(schema as ProcessTaskSchema)) {
          return false;
        }

        return !isArchived(schema) && !(schema as ProcessTaskSchema).canOnlyBePartOfFlowchart;
      })
    : [];

  const menu =
    props.schemas == null ? null : instantiableSchemas.length === 0 ? (
      <ul className="dropdown-menu pull-right">
        <li className="disabled">
          <a>No schemas configured</a>
        </li>
      </ul>
    ) : (
      <SearchableMenu
        onSelect={(schema) => {
          props.onSelect(schema);
        }}
        optionDatas={instantiableSchemas.map((schema) => ({
          value: schema,
          name: schema.name,
        }))}
        scrollable={true}
        className={css(styles.pullRight)}
      />
    );

  const tooltip = menu ? 'Create...' : 'Loading...';

  return (
    <div className="u-display-inline-block dropdown">
      <AbsoluteDropdownContainer
        onRequestClose={() => {
          setIsOpen(false);
        }}
        open={isOpen}
        menu={menu}
        shouldCloseOnInsideClick={true}
      >
        <Tooltip title={tooltip} placement={TooltipPlacement.BOTTOM} container="body">
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            disabled={!menu}
            icon="compose"
            onClick={() => {
              setIsOpen(true);
            }}
            size={ButtonSize.SMALL}
            dataTestAttrs={{component: 'CreateFromSchemaButton', element: 'root'}}
          />
        </Tooltip>
      </AbsoluteDropdownContainer>
    </div>
  );
}

const styles = StyleSheet.create({
  pullRight: {
    right: 0,
  },
});
